import { Position } from '../../../shared/types/protocol.ts';
import { getToleranceRangeStrings } from '../../../shared/utils/getToleranceRange.ts';
import { isOutOfSpecValue } from '../../../shared/utils/isOutOfSpec.ts';
import { ProtocolFinal, RecordDraft, RecordPosition } from '../../types/sharedTypeImpl.ts';
import { CmmPositionImport, CmmPositionRead } from './types.ts';


export function evalImportPositions(
    protocol: ProtocolFinal,
    record: RecordDraft,
    partIndex: number,
    positionReads: CmmPositionRead[]
): CmmPositionImport[] {
    const importIssues = positionReads.map(positionRead => {
        const position = protocol.positions.find(it => it.positionNumber === positionRead.positionNumber);
        const recordPos = record.parts[partIndex].positionValues.find(it => it.positionNumber === positionRead.positionNumber);

        return getIssues(positionRead, position, recordPos);
    });

    return importIssues;
}

function getIssues(
    positionRead: CmmPositionRead,
    position: Position | undefined,
    recordPos: RecordPosition | undefined,
): CmmPositionImport {
    let isImportable = true;
    let includeByDefault = true;

    const issues = [];
    const parsedImportedValue = parseFloat(positionRead.value);
    if (positionRead.issue) {
        issues.push(positionRead.issue);
        isImportable = false;
        includeByDefault = false;
    } else if (position == undefined || recordPos == undefined) {
        issues.push('Position not found');
        isImportable = false;
        includeByDefault = false;
    } else if (position.type !== 'measurement') {
        issues.push(`Position is ${getPositionTypeLabel(position)} in protocol, not measurement`);
        isImportable = false;
        includeByDefault = false;
    } else if (hasMissingTolerance(position)) {
        issues.push('No nominal/tolerance defined in protocol');
        isImportable = false;
        includeByDefault = false;
    } else if (Number.isNaN(parsedImportedValue) || positionRead.value.length === 0) {
        issues.push('Invalid value');
        isImportable = false;
        includeByDefault = false;
    } else {
        if (isOutOfSpecValue(position, parsedImportedValue)) {
            const { min: minString, max: maxString } = getToleranceRangeStrings(position);
            issues.push(`Out of tolerance: ${minString}...${maxString}`);
        }

        const currentValue = recordPos.value;
        if (currentValue != undefined && currentValue !== '' && currentValue !== positionRead.value) {
            issues.push(`Value already exists: ${currentValue}`);
            includeByDefault = false;
        }
    }

    return {
        positionNumber: positionRead.positionNumber,
        value: positionRead.value,
        isImportable,
        issues,
        isIncluded: includeByDefault,
    };
}

function hasMissingTolerance(position: Position): boolean {
    return position.nominal == null || position.nominal === ''
        || position.upperTolerance == null || position.upperTolerance === ''
        || position.lowerTolerance == null || position.lowerTolerance === '';
}

function getPositionTypeLabel(position: Position): string {
    switch (position.type) {
        case 'generalInspection':
            return 'general inspection';
        case 'visualInspection':
            return 'visual inspection';
        default:
            return position.type;
    }
}
